import React, { useEffect, useId, useRef, useState } from 'react';
import { useErrorReport } from '../../../contexts/ErrorReportContext';
import {
  makeExploreMediaThumb,
  optimizeExploreMedia,
} from '../../../services/exploreMediaOptimize';
import { createMemory } from '../../../services/memories';
import { getFriendsProfiles } from '../../../services/userProfile';
import GameMediaCapture from './GameMediaCapture';
import './GameSheets.css';

/**
 * Create a Memory: media/journal + optional friend invites.
 */
const GameMemoryComposeSheet = ({
  open,
  onClose,
  uid,
  friendIds = [],
  onCreated,
}) => {
  const { reportError } = useErrorReport();
  const [type, setType] = useState('photo');
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [friends, setFriends] = useState([]);
  const [selectedInviteIds, setSelectedInviteIds] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [recorderOpen, setRecorderOpen] = useState(false);
  const [pickerNonce, setPickerNonce] = useState(0);
  const closeRef = useRef(null);
  const baseId = useId();
  const libraryPhotoId = `${baseId}-memory-photo`;
  const libraryVideoId = `${baseId}-memory-video`;

  useEffect(() => {
    if (!open) return undefined;
    setType('photo');
    setTitle('');
    setText('');
    setFile(null);
    setPreviewUrl('');
    setError('');
    setBusy(false);
    setRecorderOpen(false);
    setSelectedInviteIds(new Set());
    setPickerNonce((n) => n + 1);
    closeRef.current?.focus({ preventScroll: true });
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const list = await getFriendsProfiles(friendIds);
        if (!cancelled) setFriends(list.filter(Boolean));
      } catch {
        if (!cancelled) setFriends([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, friendIds]);

  useEffect(() => {
    if (!file) {
      setPreviewUrl('');
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  if (!open) return null;

  const toggleInvite = (id) => {
    setSelectedInviteIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onPickFile = (e, mediaKind) => {
    const next = e.target.files?.[0] || null;
    e.target.value = '';
    if (!next) return;
    setType(mediaKind);
    setFile(next);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if ((type === 'photo' || type === 'video') && !file) {
      setError(type === 'photo' ? 'Choose a photo.' : 'Choose a video.');
      return;
    }
    if (type === 'journal' && !String(text || '').trim()) {
      setError('Write a short note.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      let uploadFile = file;
      let thumbFile = null;
      if (type === 'photo' || type === 'video') {
        uploadFile = await optimizeExploreMedia(type, file);
        thumbFile = await makeExploreMediaThumb(type, uploadFile);
      }
      const memory = await createMemory({
        uid,
        title,
        type,
        text,
        file: uploadFile,
        thumbFile,
        inviteUids: [...selectedInviteIds],
      });
      onCreated?.(memory);
      onClose?.();
    } catch (err) {
      reportError(err, {
        pageLabel: 'Memory',
        source: 'memory-compose',
        userMessage: err?.message || 'Could not create memory.',
      });
      setError(err?.message || 'Could not create memory.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="game-sheet" role="dialog" aria-modal="true" aria-labelledby={`${baseId}-title`}>
      <button
        type="button"
        className="game-sheet__backdrop"
        aria-label="Close"
        onClick={() => !busy && onClose?.()}
      />
      <div className="game-sheet__panel game-memory-sheet">
        <header className="game-sheet__header">
          <h2 id={`${baseId}-title`} className="game-sheet__title">
            New memory
          </h2>
          <button
            ref={closeRef}
            type="button"
            className="game-sheet__close"
            aria-label="Close"
            disabled={busy}
            onClick={() => onClose?.()}
          >
            ×
          </button>
        </header>
        <p className="game-memory-sheet__lede">
          Open for 1 day, then locks until next year’s anniversary. Friends can add anytime.
        </p>
        <form onSubmit={handleSubmit} className="game-memory-sheet__form">
          <label className="game-memory-sheet__field">
            <span>Title (optional)</span>
            <input
              type="text"
              value={title}
              maxLength={80}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Our trip…"
              disabled={busy}
            />
          </label>

          <div className="game-memory-sheet__types" role="group" aria-label="Type">
            {['photo', 'video', 'journal'].map((t) => (
              <button
                key={t}
                type="button"
                className={`game-memory-sheet__type${type === t ? ' is-active' : ''}`}
                disabled={busy}
                onClick={() => {
                  setType(t);
                  if (t === 'journal') setFile(null);
                }}
              >
                {t}
              </button>
            ))}
          </div>

          {type === 'journal' ? (
            <label className="game-memory-sheet__field">
              <span>Note</span>
              <textarea
                value={text}
                rows={4}
                maxLength={500}
                onChange={(e) => setText(e.target.value)}
                placeholder="What do you want to remember?"
                disabled={busy}
              />
            </label>
          ) : (
            <div className="game-memory-sheet__media">
              {previewUrl ? (
                type === 'video' ? (
                  // eslint-disable-next-line jsx-a11y/media-has-caption
                  <video src={previewUrl} controls playsInline className="game-memory-sheet__preview" />
                ) : (
                  <img src={previewUrl} alt="" className="game-memory-sheet__preview" />
                )
              ) : null}
              <div className="game-memory-sheet__media-actions">
                <label className="game-memory-sheet__file-btn" htmlFor={type === 'video' ? libraryVideoId : libraryPhotoId}>
                  {file ? 'Replace' : type === 'video' ? 'Choose video' : 'Choose photo'}
                </label>
                <input
                  id={libraryPhotoId}
                  key={`photo-${pickerNonce}`}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  hidden
                  onChange={(e) => onPickFile(e, 'photo')}
                />
                <input
                  id={libraryVideoId}
                  key={`video-${pickerNonce}`}
                  type="file"
                  accept="video/mp4,video/webm"
                  hidden
                  onChange={(e) => onPickFile(e, 'video')}
                />
                {type === 'video' ? (
                  <button
                    type="button"
                    className="game-memory-sheet__file-btn"
                    disabled={busy}
                    onClick={() => setRecorderOpen(true)}
                  >
                    Record
                  </button>
                ) : null}
              </div>
              <label className="game-memory-sheet__field">
                <span>Caption (optional)</span>
                <input
                  type="text"
                  value={text}
                  maxLength={200}
                  onChange={(e) => setText(e.target.value)}
                  disabled={busy}
                />
              </label>
            </div>
          )}

          {friends.length ? (
            <fieldset className="game-memory-sheet__invites">
              <legend>Invite friends</legend>
              <ul className="game-memory-sheet__friend-list">
                {friends.map((f) => {
                  const id = f.id || f.uid;
                  const checked = selectedInviteIds.has(id);
                  return (
                    <li key={id}>
                      <label className="game-memory-sheet__friend">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={busy}
                          onChange={() => toggleInvite(id)}
                        />
                        <span>{f.displayName || f.username || 'Friend'}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </fieldset>
          ) : (
            <p className="game-memory-sheet__hint">Add friends later via share link.</p>
          )}

          {error ? (
            <p className="game-memory-sheet__error" role="alert">
              {error}
            </p>
          ) : null}

          <button type="submit" className="game-memory-sheet__submit" disabled={busy}>
            {busy ? 'Creating…' : 'Create memory'}
          </button>
        </form>
      </div>
      {recorderOpen ? (
        <GameMediaCapture
          open={recorderOpen}
          onClose={() => setRecorderOpen(false)}
          onCaptured={(captured) => {
            if (!captured) return;
            setType('video');
            setFile(captured);
            setRecorderOpen(false);
          }}
          onError={(err) => {
            reportError(err, {
              pageLabel: 'Memory',
              source: 'memory-compose-record',
              userMessage: 'Could not record video.',
            });
          }}
        />
      ) : null}
    </div>
  );
};

export default GameMemoryComposeSheet;
